import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { CoachWidget } from "@/components/CoachWidget";
import Coach from "@/pages/Coach";
import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import ClubProfile from "@/pages/ClubProfile";
import CityPage from "@/pages/CityPage";
import DallasHub from "@/pages/DallasHub";
import TrainingHub from "@/pages/TrainingHub";
import ArticlePage from "@/pages/ArticlePage";
import ToolsIndex from "@/pages/ToolsIndex";
import ToolPage from "@/pages/ToolPage";
import Collabs from "@/pages/Collabs";
import Shop from "@/pages/Shop";
import ShopCollectionPage from "@/pages/ShopCollectionPage";
import ProductPage from "@/pages/ProductPage";
import PolicyPage from "@/pages/PolicyPage";
import OrderLookup from "@/pages/OrderLookup";
import Events from "@/pages/Events";
import About from "@/pages/About";
import SubmitClub from "@/pages/SubmitClub";
import JoinPage from "@/pages/JoinPage";
import Stories from "@/pages/Stories";
import StoryPage from "@/pages/StoryPage";
import Ranked from "@/pages/Ranked";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PlanBuilder from "@/pages/PlanBuilder";
import Admin from "@/pages/Admin";
import MyClubs from "@/pages/MyClubs";
import Profile from "@/pages/Profile";
import NotFound from "@/pages/NotFound";
import StatePage from "@/pages/StatePage";
import AuthCallback from "@/components/AuthCallback";
import { AuthProvider } from "@/context/AuthContext";
import { setRouteDefaults } from "@/lib/api";

const RouteMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    setRouteDefaults(pathname);
  }, [pathname]);
  return null;
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function AppRouter() {
  const location = useLocation();
  // Handle OAuth return synchronously during render (before any auth checks)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/coach" element={<Coach />} />
            <Route path="/clubs" element={<Directory />} />
            <Route path="/clubs/page/:pageNum" element={<Directory />} />
            <Route path="/clubs/:slug" element={<ClubProfile />} />
            <Route path="/run-clubs/:stateSlug" element={<StatePage />} />
            <Route path="/run-clubs/:stateSlug/:citySlug" element={<CityPage />} />
            <Route path="/dallas" element={<DallasHub />} />
            <Route path="/training" element={<TrainingHub />} />
            <Route path="/training/category/:cat" element={<TrainingHub />} />
            <Route path="/training/page/:pageNum" element={<TrainingHub />} />
            <Route path="/training/:slug" element={<ArticlePage />} />
            <Route path="/tools" element={<ToolsIndex />} />
            <Route path="/tools/:toolSlug" element={<ToolPage />} />
            <Route path="/collabs" element={<Collabs />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/state/:stateSlug" element={<ShopCollectionPage />} />
            <Route path="/shop/city-series/:citySlug" element={<ShopCollectionPage />} />
            <Route path="/shop/:collectionSlug" element={<ShopCollectionPage />} />
            <Route path="/products/:productSlug" element={<ProductPage />} />
            <Route path="/shipping" element={<PolicyPage slug="shipping" />} />
            <Route path="/returns" element={<PolicyPage slug="returns" />} />
            <Route path="/size-guide" element={<PolicyPage slug="size-guide" />} />
            <Route path="/contact" element={<PolicyPage slug="contact" />} />
            <Route path="/privacy" element={<PolicyPage slug="privacy" />} />
            <Route path="/terms" element={<PolicyPage slug="terms" />} />
            <Route path="/order-lookup" element={<OrderLookup />} />
            <Route path="/events" element={<Events />} />
            <Route path="/run" element={<Events />} />
            <Route path="/about" element={<About />} />
            <Route path="/submit" element={<SubmitClub />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/stories" element={<Stories />} />
            <Route path="/stories/:slug" element={<StoryPage />} />
            <Route path="/ranked" element={<Ranked />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/plan" element={<PlanBuilder />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/my-clubs" element={<MyClubs />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
  );
}

function App() {
  return (
    <div className="App grain bg-night text-white">
      <BrowserRouter>
        <AuthProvider>
        <ScrollManager />
        <RouteMeta />
        <Nav />
        <main className="pt-16">
          <AppRouter />
        </main>
        <Footer />
        <CoachWidget />
        <Toaster position="bottom-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
